import React from "react";
import { Link } from "react-router-dom";
import Button from "../ui/Button";
import { Card, CardBody, CardHeader } from "../ui/Card";

const FEATURES = [
  { id: "build", label: "Build estimate from photos" },
  { id: "mitigation", label: "Mitigation Station (deterministic equipment + line items + narrative)" },
  { id: "contents_corner", label: "Contents Corner (notes/photos/spreadsheets to inventory items)" },
  { id: "review", label: "Estimate review workflow" },
  { id: "labels", label: "Photo labeling workflow" },
  { id: "crm", label: "CRM workspace (jobs, contacts, activities)" },
  { id: "pipeline", label: "CRM pipeline + follow-up tracking" },
  { id: "guidelines", label: "Estimating guidelines library and job attachment" },
  { id: "insurance", label: "Insurance company contact directory" },
  { id: "code_upgrades", label: "Property code upgrade suggestions" },
  { id: "exports", label: "Estimator pack + CSV exports" },
  { id: "contents_pricing", label: "Internet replacement pricing + Xactimate contents CSV export" },
  { id: "priority", label: "Priority processing capacity" },
  { id: "single_user", label: "Single-user subscription" },
  { id: "enterprise_support", label: "Enterprise onboarding and reporting support" },
];

const TIERS = [
  {
    id: "free",
    name: "Free",
    monthly: "$0",
    annual: "$0",
    limit: "50 photo uploads total",
    included: ["build", "mitigation", "contents_corner", "review", "labels", "crm", "guidelines", "insurance"],
    cta: "Get Started",
    ctaTo: "/login",
  },
  {
    id: "starter",
    name: "Starter",
    monthly: "$19.99 / month",
    annual: "$199 / year",
    limit: "250 photo uploads / month",
    included: [
      "build",
      "mitigation",
      "contents_corner",
      "review",
      "labels",
      "crm",
      "pipeline",
      "guidelines",
      "insurance",
      "code_upgrades",
      "exports",
      "contents_pricing",
      "single_user",
    ],
    cta: "Choose Starter",
    ctaTo: "/login",
  },
  {
    id: "pro",
    name: "Pro",
    monthly: "$29.99 / month",
    annual: "$299 / year",
    limit: "750 photo uploads / month",
    included: [
      "build",
      "mitigation",
      "contents_corner",
      "review",
      "labels",
      "crm",
      "pipeline",
      "guidelines",
      "insurance",
      "code_upgrades",
      "exports",
      "contents_pricing",
      "priority",
      "single_user",
    ],
    cta: "Choose Pro",
    ctaTo: "/login",
    featured: true,
  },
  {
    id: "enterprise",
    name: "Enterprise",
    monthly: "Call for details",
    annual: "Custom annual terms",
    limit: "10,000 photo uploads / month",
    included: FEATURES.map((f) => f.id),
    cta: "Contact Sales",
    ctaTo: "mailto:sales@estimationstation.com?subject=Enterprise%20Plan%20Inquiry",
  },
];

export default function Pricing() {
  return (
    <main className="min-h-screen bg-[linear-gradient(160deg,#0b0b0b_0%,#1f1f1f_40%,#c8c8c8_100%)] px-6 py-10">
      <div className="mx-auto mb-8 grid max-w-6xl overflow-hidden rounded-3xl border border-zinc-300/80 bg-white/95 shadow-2xl lg:grid-cols-[1.1fr_1fr]">
        <div className="p-8">
          <p className="text-xs font-semibold uppercase tracking-[0.24em] text-zinc-500">Estimation Station</p>
          <h1 className="mt-2 text-4xl font-semibold tracking-tight text-zinc-900">Pricing</h1>
          <p className="mt-3 text-zinc-600">Simple tiers for individual estimators. Enterprise available on request.</p>
        </div>
        <div
          className="min-h-[200px]"
          style={{
            backgroundImage:
              "linear-gradient(to bottom, rgba(20,20,20,0.2), rgba(20,20,20,0.45)), url('https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?auto=format&fit=crop&w=1400&q=80')",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
      </div>

      <div className="mx-auto grid max-w-6xl gap-4 md:grid-cols-2 lg:grid-cols-4">
        {TIERS.map((tier) => (
          <Card
            key={tier.id}
            className={tier.featured ? "border-red-600 shadow-[0_18px_40px_-24px_rgba(220,38,38,0.45)]" : ""}
          >
            <CardHeader>
              <h2 className="text-xl font-semibold text-zinc-900">{tier.name}</h2>
            </CardHeader>
            <CardBody className="space-y-4">
              <div className="rounded-lg border border-zinc-200 bg-zinc-50 p-3 text-sm">
                <p className="text-zinc-600">
                  <span className="font-semibold text-zinc-900">Monthly:</span> {tier.monthly}
                </p>
                <p className="mt-1 text-zinc-600">
                  <span className="font-semibold text-zinc-900">Annual:</span> {tier.annual}
                </p>
              </div>
              <p className="text-sm text-zinc-700">{tier.limit}</p>

              <div className="space-y-2">
                <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700">Included</p>
                <ul className="space-y-1 text-sm text-zinc-700">
                  {FEATURES.filter((feature) => Array.isArray(tier.included) && tier.included.includes(feature.id)).map(
                    (feature) => (
                      <li key={`${tier.id}-yes-${feature.id}`} className="flex items-start gap-2">
                        <span className="mt-[1px] inline-flex min-w-[30px] items-center justify-center rounded-full bg-emerald-100 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-700">
                          YES
                        </span>
                        <span>{feature.label}</span>
                      </li>
                    )
                  )}
                </ul>
              </div>

              <div className="space-y-2">
                <p className="text-xs font-semibold uppercase tracking-wide text-zinc-500">Not Included</p>
                <ul className="space-y-1 text-sm text-zinc-700">
                  {FEATURES.filter((feature) => !(Array.isArray(tier.included) && tier.included.includes(feature.id))).map(
                    (feature) => (
                      <li key={`${tier.id}-no-${feature.id}`} className="flex items-start gap-2">
                        <span className="mt-[1px] inline-flex min-w-[30px] items-center justify-center rounded-full bg-zinc-200 px-1.5 py-0.5 text-[10px] font-semibold text-zinc-500">
                          NO
                        </span>
                        <span className="text-zinc-400 line-through">{feature.label}</span>
                      </li>
                    )
                  )}
                </ul>
              </div>

              {tier.id === "free" ? (
                <p className="text-xs text-zinc-500">
                  Free tier does not include property code upgrades, internet replacement pricing, or exports.
                </p>
              ) : null}
              {tier.id === "enterprise" ? (
                <p className="text-xs text-zinc-500">
                  Enterprise includes custom onboarding, rollout, and support terms.
                </p>
              ) : null}

              {tier.ctaTo.startsWith("mailto:") ? (
                <a href={tier.ctaTo}>
                  <Button className="w-full" variant={tier.featured ? "primary" : "secondary"}>
                    {tier.cta}
                  </Button>
                </a>
              ) : (
                <Link to={tier.ctaTo}>
                  <Button className="w-full" variant={tier.featured ? "primary" : "secondary"}>
                    {tier.cta}
                  </Button>
                </Link>
              )}
            </CardBody>
          </Card>
        ))}
      </div>

      <div className="mt-8 text-center">
        <Link to="/">
          <Button variant="secondary">Back to Home</Button>
        </Link>
      </div>
    </main>
  );
}
